import { useRef, useMemo, useState, useEffect, useCallback } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { Icosahedron, Sphere, Float } from '@react-three/drei'
import * as THREE from 'three'

/** Cached WebGL support result to avoid repeated checks */
let webGLSupportCache: boolean | null = null

/**
 * Check if the device can handle WebGL reliably.
 * Returns false for devices that commonly lose WebGL context.
 */
function canUseWebGL(): boolean {
  if (typeof window === 'undefined') return false
  if (webGLSupportCache !== null) return webGLSupportCache

  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl')
    if (!gl) {
      webGLSupportCache = false
      return false
    }

    // Check for software rendering (often unstable)
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
    if (debugInfo) {
      const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
      // Software renderers are prone to context loss
      if (/SwiftShader|Software|llvmpipe/i.test(renderer)) {
        webGLSupportCache = false
        return false
      }
    }

    // Don't call loseContext - it can cause issues with other WebGL contexts
    webGLSupportCache = true
    return true
  } catch {
    webGLSupportCache = false
    return false
  }
}

// Seeded random for deterministic particle positions
function seededRandom(seed: number) {
  const x = Math.sin(seed) * 10000
  return x - Math.floor(x)
}

function DataSwarm({ count = 200 }) {
  const swarmRef = useRef<THREE.Points>(null)

  const particlesPosition = useMemo(() => {
    const positions = new Float32Array(count * 3)
    for (let i = 0; i < count; i++) {
      // Disperse points in a spherical shell around the core using seeded random
      const r = 2.5 + seededRandom(i * 3) * 2
      const theta = 2 * Math.PI * seededRandom(i * 3 + 1)
      const phi = Math.acos(2 * seededRandom(i * 3 + 2) - 1)
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta)
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta)
      positions[i * 3 + 2] = r * Math.cos(phi)
    }
    return positions
  }, [count])

  useFrame((_, delta) => {
    if (swarmRef.current) {
      swarmRef.current.rotation.y -= delta * 0.05
      swarmRef.current.rotation.z += delta * 0.02
    }
  })

  return (
    <points ref={swarmRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[particlesPosition, 3]} />
      </bufferGeometry>
      {/* Tiny glowing points */}
      <pointsMaterial size={0.035} color="#ffffff" transparent opacity={0.6} sizeAttenuation />
    </points>
  )
}

function CoreGeometry() {
  const groupRef = useRef<THREE.Group>(null)
  const innerRef = useRef<THREE.Mesh>(null)
  const outerRef = useRef<THREE.Mesh>(null)

  useFrame((state, delta) => {
    // Intrinsic mechanical rotation
    if (innerRef.current) {
      innerRef.current.rotation.y += delta * 0.1
      innerRef.current.rotation.x += delta * 0.15
    }
    if (outerRef.current) {
      outerRef.current.rotation.y -= delta * 0.05
      outerRef.current.rotation.z += delta * 0.08
    }

    // Dynamic pointer-reactive tracking (smooth interpolation)
    if (groupRef.current) {
      // Subtle 3D perspective shift based on mouse position (-1 to 1)
      const targetX = (state.pointer.y * Math.PI) / 8
      const targetY = (state.pointer.x * Math.PI) / 8

      groupRef.current.rotation.x += (targetX - groupRef.current.rotation.x) * 0.05
      groupRef.current.rotation.y += (targetY - groupRef.current.rotation.y) * 0.05
    }
  })

  // Origin Structural Blue: #005ea1
  return (
    <Float speed={2} rotationIntensity={0.5} floatIntensity={1}>
      <group ref={groupRef}>
        {/* Inner glowing core */}
        <Sphere ref={innerRef} args={[1.5, 32, 32]}>
          <meshStandardMaterial
            color="#005ea1"
            emissive="#005ea1"
            emissiveIntensity={4}
            transparent
            opacity={0.35}
            wireframe
          />
        </Sphere>

        {/* Outer crystalline wireframe structure */}
        <Icosahedron ref={outerRef} args={[2.2, 1]}>
          <meshStandardMaterial color="#ffffff" transparent opacity={0.7} wireframe />
        </Icosahedron>

        <DataSwarm />
      </group>

      <ambientLight intensity={1} />
      <directionalLight position={[10, 10, 5]} intensity={2} color="#005ea1" />
    </Float>
  )
}

const MAX_RETRIES = 3
const RETRY_DELAY_MS = 2500
const MOUNT_DELAY_MS = 100

export function OriginCore3D() {
  const [remountKey, setRemountKey] = useState(0)
  const [ready, setReady] = useState(false)
  const retries = useRef(0)
  const [failed, setFailed] = useState(() => !canUseWebGL())

  // Delay initial mount to let other page resources load first
  useEffect(() => {
    const timer = setTimeout(() => setReady(true), MOUNT_DELAY_MS)
    return () => clearTimeout(timer)
  }, [])

  // Handle context loss with exponential backoff
  const handleContextLost = useCallback((e: Event) => {
    e.preventDefault()
    retries.current += 1
    if (retries.current <= MAX_RETRIES) {
      const delay = RETRY_DELAY_MS * retries.current // Exponential backoff
      console.warn(
        `[OriginCore3D] WebGL context lost — retry ${retries.current}/${MAX_RETRIES} in ${delay}ms`
      )
      setTimeout(() => setRemountKey((k) => k + 1), delay)
    } else {
      console.warn('[OriginCore3D] WebGL context lost — max retries reached, disabling 3D')
      setFailed(true)
    }
  }, [])

  // Handle context restored
  const handleContextRestored = useCallback(() => {
    console.info('[OriginCore3D] WebGL context restored')
    retries.current = 0
  }, [])

  if (failed || !ready) {
    // Graceful degradation — page works fine without the orb
    return null
  }

  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: -5, opacity: 0.9 }}
      aria-hidden="true"
      data-testid="origin-core-3d"
    >
      <Canvas
        key={remountKey}
        camera={{ position: [0, 0, 5.5], fov: 50 }}
        dpr={[1, 1.5]} // Reduced max DPR to lower GPU load
        gl={{
          alpha: true,
          antialias: false, // Disable for better performance
          powerPreference: 'low-power', // Prefer integrated GPU, less likely to lose context
          preserveDrawingBuffer: false,
          failIfMajorPerformanceCaveat: true, // Fail early on problematic devices
        }}
        frameloop="always" // Keep animation running
        onCreated={({ gl }) => {
          const canvas = gl.domElement
          canvas.addEventListener('webglcontextlost', handleContextLost)
          canvas.addEventListener('webglcontextrestored', handleContextRestored)
        }}
      >
        <CoreGeometry />
        {/* Removed Environment preset and post-processing to reduce GPU load and prevent context loss */}
      </Canvas>
    </div>
  )
}
